import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InterestsIcon from '@mui/icons-material/Interests';
import SaveIcon from '@mui/icons-material/Save';
import SectionCard from './SectionCard';
import { INTEREST_SUGGESTIONS } from './profileConstants';
import userService from '../../services/userService';

interface InterestsCardProps {
  notify: (message: string) => void;
}

/** Areas-of-interest chips with suggestions, custom input, and save. */
export default function InterestsCard({ notify }: InterestsCardProps) {
  const [interests, setInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    userService.getInterests()
      .then(setInterests)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || interests.includes(trimmed)) return;
    setInterests(prev => [...prev, trimmed]);
    setInput('');
  };

  const handleRemove = (interest: string) => {
    setInterests(prev => prev.filter(i => i !== interest));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const updated = await userService.updateInterests(interests);
      setInterests(updated);
      notify('Interests saved');
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      icon={<InterestsIcon />}
      title="Areas of Interest"
      subtitle="Select topics that matter to you; we'll use them to personalize your CPE recommendations."
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          Failed to load your interests.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {interests.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {interests.map(interest => (
                <Chip
                  key={interest}
                  label={interest}
                  onDelete={() => handleRemove(interest)}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          )}

          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              Suggestions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              {INTEREST_SUGGESTIONS.filter(s => !interests.includes(s)).map(suggestion => (
                <Chip
                  key={suggestion}
                  label={suggestion}
                  onClick={() => handleAdd(suggestion)}
                  icon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="Add custom interest"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(input); } }}
              size="small"
              sx={{ width: 280 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleAdd(input)}
              disabled={!input.trim()}
              sx={{ alignSelf: 'center' }}
            >
              Add
            </Button>
          </Box>

          {saveError && <Alert severity="error" sx={{ maxWidth: 480 }}>{saveError}</Alert>}

          <Box>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Save Interests
            </Button>
          </Box>
        </Stack>
      )}
    </SectionCard>
  );
}
